import { Paper, ScrollArea } from "@mantine/core";
import { type CSSProperties, type ReactNode, useMemo, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { MainPanel } from "@/panels/modules/panels/components/MainPanel";
import { PanelErrorFallback } from "@/panels/modules/panels/components/PanelErrorFallback";
import {
    calculateLeftPanelSize,
    calculateTopPanelSize,
    computePanelSizes,
    computePanelStates,
} from "@/panels/modules/panels/components/PanelsView.helpers";
import { usePanelCollapse } from "@/panels/modules/panels/hooks/usePanelCollapse";
import { usePanelResize } from "@/panels/modules/panels/hooks/usePanelResize";
import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";
import styles from "./PanelsView.module.css";

const LEFT_MAIN_PANEL_GROUP_ID = "leftMainPanelGroup";
const RIGHT_MAIN_PANEL_GROUP_ID = "rightMainPanelGroup";
const LOCK_ASIDE_PANELS = true;

interface PanelsViewProps {
    // Content slots
    leftSidebar?: ReactNode;
    topbar?: ReactNode;
    rightSidebar?: ReactNode;
    leftTopA?: ReactNode;
    leftTopB?: ReactNode;
    leftBottomA?: ReactNode;
    leftBottomB?: ReactNode;
    rightTopA?: ReactNode;
    rightTopB?: ReactNode;
    rightBottomA?: ReactNode;
    rightBottomB?: ReactNode;

    // Panel configs
    leftSidebarConfig: AsidePanelConfig;
    topbarConfig: TopbarConfig;
    rightSidebarConfig: AsidePanelConfig;
    leftTopConfig: MainPanelConfig;
    leftBottomConfig: MainPanelConfig;
    rightTopConfig: MainPanelConfig;
    rightBottomConfig: MainPanelConfig;

    // Behavior props
    lockMainPanelResize?: boolean;
}

export const PanelsView = ({
    leftSidebar,
    topbar,
    rightSidebar,
    leftTopA,
    leftTopB,
    leftBottomA,
    leftBottomB,
    rightTopA,
    rightTopB,
    rightBottomA,
    rightBottomB,
    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,
    lockMainPanelResize = false,
}: PanelsViewProps) => {
    const sizes = useMemo(
        () => computePanelSizes(topbarConfig, leftSidebarConfig, rightSidebarConfig),
        [topbarConfig, leftSidebarConfig, rightSidebarConfig],
    );

    const leftAsidePanelRef = useRef<ImperativePanelHandle>(null);
    const topbarPanelRef = useRef<ImperativePanelHandle>(null);
    const rightAsidePanelRef = useRef<ImperativePanelHandle>(null);
    const leftMainPanelGroupRef = useRef<ImperativePanelHandle>(null);
    const rightMainPanelGroupRef = useRef<ImperativePanelHandle>(null);
    const leftTopPanelRef = useRef<ImperativePanelHandle>(null);
    const leftBottomPanelRef = useRef<ImperativePanelHandle>(null);
    const rightTopPanelRef = useRef<ImperativePanelHandle>(null);
    const rightBottomPanelRef = useRef<ImperativePanelHandle>(null);

    // Compute all panel states and collapsed heights
    const {
        isLeftMainPanelFullyClosedValue,
        isRightMainPanelFullyClosedValue,
        isOneLeftMainPanelFullyClosedValue,
        isOneRightMainPanelFullyClosedValue,
        isOneSideOfMainPanelsFullyClosedValue,
        leftTopCollapsedHeightPixels,
        rightTopCollapsedHeightPixels,
        leftBottomCollapsedHeightPixels,
        rightBottomCollapsedHeightPixels,
    } = computePanelStates(
        leftTopConfig,
        leftBottomConfig,
        rightTopConfig,
        rightBottomConfig,
        leftTopA,
        leftTopB,
        leftBottomA,
        leftBottomB,
        rightTopA,
        rightTopB,
        rightBottomA,
        rightBottomB,
    );

    usePanelCollapse({
        panelRef: leftAsidePanelRef,
        isCollapsed: leftSidebarConfig.isCollapsed,
    });

    usePanelCollapse({
        panelRef: topbarPanelRef,
        isCollapsed: topbarConfig.isCollapsed,
    });

    usePanelCollapse({
        panelRef: rightAsidePanelRef,
        isCollapsed: rightSidebarConfig.isCollapsed,
    });

    usePanelResize({
        panelRef: leftMainPanelGroupRef,
        size: calculateLeftPanelSize(isLeftMainPanelFullyClosedValue, isRightMainPanelFullyClosedValue),
        dependencies: [isLeftMainPanelFullyClosedValue, isRightMainPanelFullyClosedValue],
    });

    usePanelResize({
        panelRef: leftTopPanelRef,
        size: calculateTopPanelSize(leftTopConfig, leftBottomConfig),
        dependencies: [leftTopConfig, leftBottomConfig],
    });

    usePanelResize({
        panelRef: rightTopPanelRef,
        size: calculateTopPanelSize(rightTopConfig, rightBottomConfig),
        dependencies: [rightTopConfig, rightBottomConfig],
    });

    return (
        <PanelGroup direction="horizontal">
            <Panel
                ref={leftAsidePanelRef}
                defaultSize={sizes.leftSidebar.defaultSize}
                collapsible={true}
                style={{
                    flexBasis: leftSidebarConfig.isCollapsed ? 0 : undefined,
                    minWidth: leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.minWidth,
                    maxWidth: leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.maxWidth,
                }}
            >
                <Paper radius={0} h="100%" className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>

            <PanelResizeHandle
                disabled={LOCK_ASIDE_PANELS}
                hitAreaMargins={{
                    coarse: 8,
                    fine: 4,
                }}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel className={styles.mainPanelContainer}>
                <PanelGroup direction={"vertical"}>
                    <Panel
                        ref={topbarPanelRef}
                        collapsible={true}
                        defaultSize={sizes.topbar.defaultSize}
                        style={(() => {
                            const style: CSSProperties = {};

                            if (topbarConfig.flexShrink !== undefined) style.flexShrink = topbarConfig.flexShrink;
                            if (topbarConfig.flexGrow !== undefined) style.flexGrow = topbarConfig.flexGrow;

                            if (topbarConfig.isCollapsed) {
                                style.flexBasis = 0;
                                style.minHeight = 0;
                                style.maxHeight = 0;
                            } else {
                                if (sizes.topbar.defaultSize !== undefined) style.flexBasis = sizes.topbar.defaultSize;
                                if (sizes.topbar.minHeight !== undefined) style.minHeight = sizes.topbar.minHeight;
                                if (sizes.topbar.maxHeight !== undefined) style.maxHeight = sizes.topbar.maxHeight;
                            }
                            return style;
                        })()}
                    >
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ScrollArea className={styles.scrollArea}>
                                <ErrorBoundary fallbackRender={PanelErrorFallback}>{topbar}</ErrorBoundary>
                            </ScrollArea>
                        </Paper>
                    </Panel>

                    <PanelResizeHandle
                        disabled={LOCK_ASIDE_PANELS}
                        hitAreaMargins={{
                            coarse: 8,
                            fine: 4,
                        }}
                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical}`}
                    />

                    <Panel minSize={sizes.mainPanels.minSize}>
                        <PanelGroup direction="horizontal">
                            <Panel
                                ref={leftMainPanelGroupRef}
                                id={LEFT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={true}
                                minSize={sizes.mainPanels.minSize}
                            >
                                <PanelGroup direction="vertical">
                                    <Panel
                                        id="leftTop"
                                        ref={leftTopPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: leftTopCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel panelId={"leftTop"} tabASlot={leftTopA} tabBSlot={leftTopB} />
                                    </Panel>

                                    <PanelResizeHandle
                                        hitAreaMargins={{
                                            coarse: 8,
                                            fine: 4,
                                        }}
                                        disabled={isOneLeftMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneLeftMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="leftBottom"
                                        ref={leftBottomPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: leftBottomCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"leftBottom"}
                                            tabASlot={leftBottomA}
                                            tabBSlot={leftBottomB}
                                        />
                                    </Panel>
                                </PanelGroup>
                            </Panel>

                            <PanelResizeHandle
                                hitAreaMargins={{
                                    coarse: 8,
                                    fine: 4,
                                }}
                                disabled={lockMainPanelResize || isOneSideOfMainPanelsFullyClosedValue}
                                className={`${styles.panelResizeHandle} ${!isOneSideOfMainPanelsFullyClosedValue && styles.panelResizeHandleHorizontal}`}
                            />

                            <Panel
                                ref={rightMainPanelGroupRef}
                                id={RIGHT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={true}
                                minSize={sizes.mainPanels.minSize}
                            >
                                <PanelGroup direction="vertical">
                                    <Panel
                                        id="rightTop"
                                        ref={rightTopPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: rightTopCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel panelId={"rightTop"} tabASlot={rightTopA} tabBSlot={rightTopB} />
                                    </Panel>

                                    <PanelResizeHandle
                                        hitAreaMargins={{
                                            coarse: 8,
                                            fine: 4,
                                        }}
                                        disabled={isOneRightMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneRightMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="rightBottom"
                                        ref={rightBottomPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: rightBottomCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"rightBottom"}
                                            tabASlot={rightBottomA}
                                            tabBSlot={rightBottomB}
                                        />
                                    </Panel>
                                </PanelGroup>
                            </Panel>
                        </PanelGroup>
                    </Panel>
                </PanelGroup>
            </Panel>

            <PanelResizeHandle
                disabled={LOCK_ASIDE_PANELS}
                hitAreaMargins={{
                    coarse: 8,
                    fine: 4,
                }}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel
                ref={rightAsidePanelRef}
                defaultSize={sizes.rightSidebar.defaultSize}
                collapsible={true}
                style={{
                    flexBasis: rightSidebarConfig.isCollapsed ? 0 : undefined,
                    minWidth: rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.minWidth,
                    maxWidth: rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.maxWidth,
                }}
            >
                <Paper radius={0} h={"100%"} className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{rightSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>
        </PanelGroup>
    );
};
